<template>
  <div id="OrgSettled" class="org-settled-page bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors duration-300">
    <!-- 顶部导航栏 -->
    <nav-bar class="about-nav-bar" v-if="!isH5" />

    <!-- ① Hero Section -->
    <section class="relative pt-6 md:pt-24 pb-10 md:pb-32 overflow-hidden bg-slate-50 dark:bg-slate-950 transition-colors duration-300" :style="heroSectionStyle">
      <!-- 装饰背景 -->
      <div class="absolute inset-0 z-0 pointer-events-none">
        <div class="absolute inset-0 bg-gradient-to-b from-slate-100 via-white to-white dark:from-slate-900 dark:via-slate-950 dark:to-slate-950 transition-colors duration-300"></div>
        <div class="absolute top-[-20%] right-[5%] w-[800px] h-[800px] bg-brand-500/10 dark:bg-brand-500/10 rounded-full blur-[120px] animate-pulse"></div>
        <div class="absolute bottom-[0%] left-[0%] w-[600px] h-[600px] bg-blue-500/10 dark:bg-blue-600/10 rounded-full blur-[100px]"></div>
      </div>

      <div class="max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
        <h1 class="text-[28px] sm:text-5xl md:text-7xl font-black text-slate-900 dark:text-white mb-4 md:mb-8 tracking-tight leading-[1.3] md:leading-[1.1]">
          {{ heroTitleDisplay }}
        </h1>
        <p class="text-slate-600 dark:text-slate-400 text-[13px] sm:text-lg md:text-2xl max-w-3xl mx-auto mb-2 md:mb-12 leading-relaxed font-medium px-4 md:px-0">
          {{ aiPlatform.desc }}
        </p>

        <!-- CTA Buttons -->
        <div class="flex flex-col sm:flex-row items-center justify-center gap-1 md:gap-5 mb-3 md:mb-16 px-4 md:px-0">
          <button
            @click="handleFreeTrial"
            class="w-full sm:w-auto px-6 md:px-10 h-4 md:h-[auto] md:py-5 bg-brand-600 hover:bg-brand-500 text-white rounded-full font-bold text-[15px] md:text-xl shadow-xl shadow-brand-500/30 transition-all hover:-translate-y-1 hover:shadow-brand-500/50 border-none cursor-pointer flex items-center justify-center leading-none"
          >
            免费试用
          </button>
          <button
            @click="handleSettle"
            class="mt-1 sm:mt-0 w-full sm:w-auto px-6 md:px-10 h-4 md:h-[auto] md:py-5 bg-white dark:bg-white/5 hover:bg-slate-50 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-white rounded-full font-bold text-[15px] md:text-xl backdrop-blur-sm transition-all flex items-center justify-center gap-1.5 md:gap-2 shadow-lg dark:shadow-none hover:-translate-y-1 cursor-pointer leading-none"
          >
            立即咨询
            <svg class="w-[18px] h-[18px] md:w-5 md:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </button>
        </div>

        <!-- 平台截图 -->
        <img
          :src="aiPlatformImg"
          :alt="aiPlatform.name"
          ref="heroImage"
          class="w-[calc(100%-32px)] md:w-full max-w-[1136px] mx-auto rounded-xl md:rounded-3xl shadow-2xl dark:shadow-slate-900/50 object-cover hero-image"
          :class="{ 'hero-image--highlight': heroImageHighlighted }"
          loading="lazy"
        />
      </div>
    </section>

    <!-- ② 直播间模块 -->
    <section v-if="liveRoom" class="py-3 md:py-24 bg-white dark:bg-slate-900 border-y border-slate-100 dark:border-slate-800 transition-colors duration-300">
      <div class="max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="sm:text-3xl md:text-5xl font-black text-slate-900 dark:text-white mb-2 md:mb-12">{{ liveRoom.name }}</h2>

        <div class="flex flex-col lg:flex-row gap-4 md:gap-12">
          <!-- Tabs -->
          <div class="lg:w-[380px] flex-shrink-0 flex flex-col gap-1 md:gap-3" role="tablist" aria-label="直播间类型">
            <button
              v-for="(item, index) in liveRoom.list"
              :key="item.name + index"
              class="w-full text-left p-2 md:p-6 rounded-xl md:rounded-2xl border transition-all duration-300 cursor-pointer"
              :class="activeLiveRoomIndex === index
                ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-800 shadow-lg shadow-brand-500/10'
                : 'bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-700 hover:border-slate-200 dark:hover:border-slate-600 hover:shadow-md'"
              role="tab"
              :aria-selected="activeLiveRoomIndex === index"
              :tabindex="activeLiveRoomIndex === index ? 0 : -1"
              @click="handleLiveRoomChange(index)"
              @keydown="handleLiveRoomKeydown($event, index)"
            >
              <span class="block text-[15px] md:text-lg font-bold" :class="activeLiveRoomIndex === index ? 'text-brand-600 dark:text-brand-400' : 'text-slate-800 dark:text-slate-200'">{{ item.name }}</span>
              <span
                v-if="activeLiveRoomIndex === index && item.desc"
                class="block mt-1.5 md:mt-2 text-[13px] md:text-sm text-slate-500 dark:text-slate-400 leading-[1.6] md:leading-relaxed"
              >
                {{ item.desc }}
              </span>
            </button>
          </div>

          <!-- Image -->
          <div class="flex-1" v-if="activeLiveRoomItem">
            <div class="bg-slate-50 dark:bg-slate-800 rounded-xl md:rounded-3xl p-2 md:p-4 shadow-inner dark:shadow-none">
              <img
                :src="activeLiveRoomItem.img"
                :alt="activeLiveRoomItem.name"
                class="w-full rounded-2xl object-cover shadow-lg"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ③ AI 驱动效率模块 -->
    <section v-if="aiModule" class="py-10 md:py-24 relative overflow-hidden bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      <!-- 旋转装饰 -->
      <div
        class="ssr-variant hidden-1c0dkx6 hidden md:block"
        :style="{ '--rotate-angle': rotateAngle + 'deg' }"
      >
        <div class="framer-1stzqw2-container">
          <div class="framer-gzvjR framer-143nf7r framer-v-143nf7r">
            <div class="framer-1ehqw8y"></div>
            <div class="framer-1bce4jm"></div>
            <div class="framer-xv7igh"></div>
            <div class="framer-xknqa4"></div>
            <div class="framer-1p1pzny"></div>
            <div class="framer-166olkj">
              <div class="framer-1socfl3"></div>
              <div class="framer-y8aghn"></div>
              <div class="framer-1w9dmf0"></div>
              <div class="framer-88dz9y"></div>
              <div class="framer-trql9i"></div>
              <div class="framer-138py8g"></div>
              <div class="framer-1yx3zku"></div>
            </div>
            <div class="framer-19bj41o"></div>
          </div>
        </div>
      </div>

      <div class="max-w-[1520px] mx-auto px-1 sm:px-6 lg:px-8 relative z-10">
        <div class="text-center mb-8 md:mb-16">
          <span class="inline-flex items-center gap-1.5 px-3 md:px-5 py-1 md:py-2 rounded-full border border-brand-500/20 bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-[11px] md:text-sm font-bold mb-4 md:mb-6 backdrop-blur-md shadow-sm">
            {{ aiModule.name }}
          </span>
          <h2 class="sm:text-3xl md:text-5xl font-black text-slate-900 dark:text-white px-2 md:px-0">{{ aiModule.desc }}</h2>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8 px-0 md:px-0">
          <div
            v-for="(item, index) in aiModule.list"
            :key="item.name + index"
            class="bg-white dark:bg-white/[0.03] hover:bg-white dark:hover:bg-white/[0.06] border border-slate-100 dark:border-white/5 hover:border-brand-200 dark:hover:border-white/10 p-2 md:p-8 rounded-2xl md:rounded-[2.5rem] transition-all duration-300 group shadow-sm hover:shadow-xl dark:shadow-none hover:-translate-y-2 flex flex-col"
          >
            <div class="mb-3 md:mb-6">
              <img :src="item.img" :alt="item.name" class="w-10 h-10 md:w-28 md:h-28 rounded-xl md:rounded-2xl object-cover" loading="lazy" />
            </div>
            <h3 class="text-[15px] md:text-2xl font-bold text-slate-800 dark:text-white mb-1 md:mb-2 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">{{ item.name }}</h3>
            <p class="text-[12px] md:text-sm font-medium text-brand-600 dark:text-brand-400 mb-1.5 md:mb-3">{{ item.desc }}</p>
            <p class="text-slate-500 dark:text-slate-400 leading-[1.6] md:leading-relaxed text-[13px] md:text-base">{{ item.content }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ④ 作业系统 -->
    <section v-if="homework" class="py-3 md:py-24 bg-white dark:bg-slate-900 transition-colors duration-300">
      <div class="max-w-[1520px] mx-auto px-1 sm:px-6 lg:px-8">
        <h2 class="sm:text-3xl md:text-5xl font-black text-slate-900 dark:text-white mb-2 md:mb-12 text-center">{{ homework.name }}</h2>

        <!-- Tabs -->
        <div class="flex items-center justify-center flex-wrap gap-2 md:gap-2 mb-6 md:mb-12" role="tablist" aria-label="课后支持模块">
          <button
            v-for="(item, index) in homework.list"
            :key="item.name + index"
            class="px-1 md:px-8 h-[36px] md:h-auto md:py-3 rounded-full font-bold text-[13px] md:text-base transition-all duration-300 border cursor-pointer flex items-center justify-center leading-none"
            :class="activeHomeworkIndex === index
              ? 'bg-slate-900 dark:bg-brand-600 text-white border-slate-900 dark:border-brand-600 shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'"
            role="tab"
            :aria-selected="activeHomeworkIndex === index"
            :tabindex="activeHomeworkIndex === index ? 0 : -1"
            @click="handleHomeworkTabChange(index)"
            @keydown="handleHomeworkKeydown($event, index)"
          >
            {{ item.name }}
          </button>
        </div>

        <!-- Panel -->
        <transition name="homework-slide" mode="out-in">
          <div
            v-if="activeHomeworkItem"
            :key="activeHomeworkIndex"
            class="flex flex-col lg:flex-row gap-5 md:gap-12 items-center bg-slate-50 dark:bg-slate-800/50 rounded-2xl md:rounded-[3rem] p-4 md:p-12 border border-slate-100 dark:border-slate-700 w-full"
          >
            <div class="lg:w-1/2">
              <h3 class="text-[18px] md:text-3xl font-bold text-slate-800 dark:text-white mb-2 md:mb-4">
                {{ activeHomeworkItem.name }}
              </h3>
              <p v-if="activeHomeworkItem.desc" class="text-brand-600 dark:text-brand-400 font-medium text-[13px] md:text-base mb-2 md:mb-4">
                {{ activeHomeworkItem.desc }}
              </p>
              <p class="text-slate-500 dark:text-slate-400 leading-relaxed mb-4 md:mb-6 text-[13px] md:text-lg">
                {{ activeHomeworkItem.content }}
              </p>
              <ul
                v-if="activeHomeworkItem.tags && activeHomeworkItem.tags.length"
                class="flex flex-wrap gap-1.5 md:gap-2"
              >
                <li
                  v-for="(tag, tagIndex) in activeHomeworkItem.tags"
                  :key="tag + tagIndex"
                  class="inline-flex items-center gap-1 md:gap-1.5 px-2.5 md:px-4 py-1 md:py-2 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 rounded-full text-[12px] md:text-sm font-bold border border-brand-100 dark:border-brand-800/30"
                >
                  <svg class="w-3 h-3 md:w-3.5 md:h-3.5 text-brand-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {{ tag }}
                </li>
              </ul>
            </div>
            <div class="lg:w-1/2 w-full">
              <div class="bg-white dark:bg-slate-800 rounded-xl md:rounded-3xl shadow-xl dark:shadow-slate-900/50 p-1.5 md:p-4 border border-slate-100 dark:border-slate-700">
                <img
                  :src="activeHomeworkItem.img"
                  :alt="activeHomeworkItem.name"
                  class="w-full rounded-lg md:rounded-2xl object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </transition>
      </div>
    </section>

    <!-- ⑤ 真实用户故事 -->
    <section v-if="realStories" class="py-10 md:py-24 bg-slate-50 dark:bg-slate-950 overflow-hidden transition-colors duration-300">
      <div class="max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-[20px] sm:text-3xl md:text-5xl font-black text-slate-900 dark:text-white mb-8 md:mb-16 text-center">{{ realStories.name }}</h2>
      </div>

      <div class="stories-marquee">
        <div class="stories-row stories-row--top">
          <div class="stories-track">
            <article
              v-for="(item, index) in storiesTopRowLoop"
              :key="`top-${index}-${item.name}`"
              class="story-card w-[240px] md:w-[320px] bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 rounded-xl md:rounded-3xl p-4 md:p-6 hover:shadow-xl dark:hover:shadow-slate-900/50 transition-all duration-300"
            >
              <p class="text-[13px] md:text-base text-slate-600 dark:text-slate-300 leading-relaxed mb-3 md:mb-4" v-html="item.desc"></p>
              <div class="flex items-center gap-2 md:gap-3">
                <img
                  :src="item.avatar"
                  :alt="item.name"
                  class="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover border-2 border-brand-100 dark:border-brand-900/30"
                  loading="lazy"
                />
                <div>
                  <p class="text-[13px] md:text-sm font-bold text-slate-800 dark:text-slate-100">{{ item.name }}</p>
                  <p class="text-[11px] md:text-xs text-slate-400 dark:text-slate-500 font-medium">{{ item.position }}</p>
                </div>
              </div>
            </article>
          </div>
        </div>
        <div class="stories-row stories-row--bottom">
          <div class="stories-track">
            <article
              v-for="(item, index) in storiesBottomRowLoop"
              :key="`bottom-${index}-${item.name}`"
              class="story-card w-[240px] md:w-[320px] bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 rounded-xl md:rounded-3xl p-4 md:p-6 hover:shadow-xl dark:hover:shadow-slate-900/50 transition-all duration-300"
            >
              <p class="text-[13px] md:text-base text-slate-600 dark:text-slate-300 leading-relaxed mb-3 md:mb-4" v-html="item.desc"></p>
              <div class="flex items-center gap-2 md:gap-3">
                <img
                  :src="item.avatar"
                  :alt="item.name"
                  class="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover border-2 border-brand-100 dark:border-brand-900/30"
                  loading="lazy"
                />
                <div>
                  <p class="text-[13px] md:text-sm font-bold text-slate-800 dark:text-slate-100">{{ item.name }}</p>
                  <p class="text-[11px] md:text-xs text-slate-400 dark:text-slate-500 font-medium">{{ item.position }}</p>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>

    <!-- ⑥ FAQ -->
    <section v-if="qa" class="py-4 md:py-24 bg-white dark:bg-slate-900 transition-colors duration-300">
      <div class="max-w-[900px] mx-auto px-1 sm:px-6 lg:px-8">
        <h2 class="sm:text-3xl md:text-5xl font-black text-slate-900 dark:text-white mb-2 md:mb-16 text-center">常见问题(FAQs)</h2>

        <div class="space-y-4">
          <div
            v-for="(item, index) in qa.list"
            :key="index"
            class="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 overflow-hidden transition-all duration-300 shadow-sm"
            :class="{ 'shadow-md border-brand-200 dark:border-brand-800': activeQaIndexes.includes(index) }"
          >
            <div
              class="flex items-center justify-between px-1 py-2 cursor-pointer select-none group"
              @click="handleQaItemClick(index)"
            >
              <p class="md:text-lg font-bold text-slate-800 dark:text-slate-100 pr-4">{{ item.Q }}</p>
              <div class="flex-shrink-0 w-3 h-3 md:w-8 md:h-8  rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center transition-all duration-300 group-hover:bg-brand-50 dark:group-hover:bg-brand-900/30"
                :class="{ 'bg-brand-50 dark:bg-brand-900/30': activeQaIndexes.includes(index) }">
                <svg
                  width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
                  class="transition-transform duration-300 text-slate-500 dark:text-slate-400"
                  :class="{ 'rotate-180 text-brand-600 dark:text-brand-400': activeQaIndexes.includes(index) }"
                ><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>
            <div
              class="qa-answer overflow-hidden transition-all duration-300"
              :class="activeQaIndexes.includes(index) ? 'qa-answer--open' : 'qa-answer--closed'"
            >
              <div class=" px-1 pb-2 md:px-6 md:pb-5">
                <p
                  class="text-slate-500 dark:text-slate-400 leading-relaxed md:text-base qa-answer-text"
                  @click="handleFreeTrial"
                  v-html="item.A"
                ></p>
              </div>
            </div>
          </div>
        </div>

        <!-- 联系区 -->
        <div class="mt-4 md:mt-12 bg-brand-50 dark:bg-brand-900/10 rounded-2xl md:rounded-3xl p-2 md:p-8 flex flex-col md:flex-row items-center justify-between gap-4 border border-brand-100 dark:border-brand-800/30">
          <div>
            <p class="md:text-xl font-bold text-slate-800 dark:text-slate-100 mb-1">找不到您的答案吗?</p>
            <p class="md:text-sm text-slate-500 dark:text-slate-400">请与我们联系，我们很乐意为您提供帮助！</p>
          </div>
          <button
            @click="handleSettle"
            class="px-2 py-1 md:px-8 md:py-3 bg-brand-600 hover:bg-brand-500 text-white rounded-full font-bold md:text-base shadow-lg shadow-brand-500/20 transition-all hover:-translate-y-0.5 border-none cursor-pointer whitespace-nowrap"
          >
            立即咨询
          </button>
        </div>
      </div>
    </section>

    <!-- ⑦ 微信二维码区域 -->
    <section v-if="wechatEntry" class="py-3 md:py-24 bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      <div class="max-w-[1520px] mx-auto px-2 sm:px-6 lg:px-8">
        <div class="bg-white dark:bg-gradient-to-br dark:from-brand-950 dark:to-slate-950 rounded-2xl md:rounded-3xl p-2 md:p-16 border border-slate-200 dark:border-white/10 shadow-2xl relative overflow-hidden">
          <!-- 装饰光晕 -->
          <div class="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-500/10 dark:bg-brand-500/10 rounded-full blur-[100px] translate-x-1/3 -translate-y-1/3 pointer-events-none"></div>

          <div class="relative z-10 text-center">
            <h2 class="text-[28px] sm:text-3xl md:text-4xl font-black text-slate-900 dark:text-white mb-3 md:mb-10 max-w-3xl mx-auto leading-tight tracking-tight">
              {{ wechatEntry.name }}
            </h2>

            <div class="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
              <div class="bg-white dark:bg-slate-900/50 md:p-4 md:rounded-2xl shadow-lg dark:shadow-none border border-slate-100 dark:border-slate-700">
                <img
                  :src="wechatEntry.qr_code"
                  alt="微信二维码"
                  class="w-20 h-20 md:w-48 md:h-48 object-contain"
                  loading="lazy"
                />
              </div>
              <div class="text-center md:text-left">
                <p
                  v-for="(line, index) in wechatEntryDescLines"
                  :key="index"
                  class="text-slate-600 dark:text-slate-300 md:text-lg mb-2 font-medium"
                >
                  {{ line }}
                </p>
                <button
                  @click="handleFreeTrial"
                  class="mt-1 md:mt-6 px-4 py-1 md:px-10 md:py-4 bg-slate-900 dark:bg-brand-600 hover:bg-brand-600 dark:hover:bg-brand-500 text-white font-bold rounded-full shadow-xl shadow-brand-500/20 transition-all hover:scale-[1.02] md:text-lg border-none cursor-pointer"
                >
                  免费试用
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 咨询入口 -->
    <Consult :visible.sync="consultVisible" ref="Consult" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getUserInfo } from "@saas/server-api/src";
import Consult from "./components/Consult.vue";
import { NavBar, isH5, isMobile, getCdnUrl } from "@saas/components/src";
import { getOrgSettledJson } from "@saas/server-api/src/json";
import { fastCreateOrganization } from "@saas/server-api/src/organization";

export default {
  name: "OrgSettled",
  components: {
    Consult,
    NavBar,
  },
  data: function () {
    return {
      isH5: isH5(),
      jsonData: [],
      activeQaIndexes: [],
      activeLiveRoomIndex: 0,
      activeHomeworkIndex: 0,
      heroTitleDisplay: "教育直播 Al盈利赋能平台",
      heroTitleAnimationTimer: null,
      heroImageHighlighted: false,
      heroImageScrollHandler: null,
      consultVisible: false,
      rotateAngle: 0,
      rotateAnimationTimer: null,
      homeworkAutoSwitchTimer: null,
      aiPlatform: {
        name: "教育直播 Al盈利赋能平台",
        desc: "用智能驱动增效、靠技术促增长，全链路助力机构快速盈利赋能平台 ",
        bg_img: getCdnUrl("/cdn/org/settled/ai-platform-bg.png"),
        img: getCdnUrl("/cdn/org/settled/ai-platform.png"),
      },
      liveRoom: {
        key: "LiveRoom",
        name: "直播间",
        list: [
          {
            name: "大班课",
            desc: "支持万人同时在线，延迟小于 1 秒，适用于公开课、引流课、大型讲座，可快速扩大品牌影响力",
            img: getCdnUrl("/cdn/org/settled/live-room-big-class.png"),
          },
          {
            name: "小班课",
            desc: "支持 1V1 至 1V12 多班型，低延迟直播；老师能管理课堂权限、添加课件，课中可使用定时、计时、答题器及共享白板，满足老师多样化教学需求。",
            img: getCdnUrl("/cdn/org/settled/live-room-small-class.png"),
          },
          {
            name: "版权保护",
            desc: "通过显示机构水印等跑马灯结合防录屏、网页防火墙及版权声明等手段，多维度筑牢防护，显著降低内容版权安全风险。",
            img: getCdnUrl("/cdn/org/settled/live-room-copyright.png"),
          },
        ],
      },
      aiModule: {
        key: "AIModule",
        name: "AI - 驱动效率",
        desc: "AI 智能梳课重点、解课时疑问，助力高效学习",
        list: [
          {
            name: "AI 看课",
            desc: "提高学习效果，快速吸收知识点",
            content:
              "回放课时支持 AI 总结及提问。学生不用反复回看找重点，复习效率翻倍，减轻老师答疑压力。",
            img: getCdnUrl("/cdn/org/settled/live-module-ai-look.png"),
          },
          {
            name: "AI 笔记",
            desc: "帮助快速整理记录学习笔记和知识点",
            content:
              "AI 自动同步课程内容生成精准笔记，回放时可随时将 AI 笔记插入个人笔记，补充关键知识点辅助理解课程内容，学习更轻松。",
            img: getCdnUrl("/cdn/org/settled/live-module-ai-note.png"),
          },
          {
            name: "AI 录题",
            desc: "AI 批量导入题目，提高识别准确度",
            content:
              "批量导入题目，精准识别内容，提升录题效率，快速构建专属题库为教师减负增效。",
            img: getCdnUrl("/cdn/org/settled/live-module-ai-question.png"),
          },
        ],
      },
      homework: {
        key: "Homework",
        name: "作业布置、学习打卡与试题课后一体化支撑",
        bg_img: getCdnUrl("/cdn/org/settled/homework-bg.png"),
        list: [
          {
            name: "作业系统",
            content:
              "老师可以像线下教学一样,给学生布置课后作业,作业包含多多个类型,可自动批阅及查看数据,功能齐全又强大!",
            img: getCdnUrl("/cdn/org/settled/homework-system.png"),
            tags: ["丰富作业类型系统", "自动识别题型", "高效智能批改"],
          },
          {
            name: "打卡系统",
            content:
              "系统支持每日打卡和闯关挑战，激励培养学习习惯，让坚持变得轻松有趣！",
            img: getCdnUrl("/cdn/org/settled/homework-check.png"),
            tags: ["每日打卡", "闯关打卡", "趣味学习"],
          },
          {
            name: "题库与试卷",
            content:
              "支持一键导入题目生成作业，课程作业、随堂测验等场景可直接复用作业模板，无需重复创建，让备课、测评更省心。",
            img: getCdnUrl("/cdn/org/settled/homework-ai-question.png"),
            tags: [
              "导入、编辑、分类，题目可存库",
              "自动识别题型",
              "自动/手动组卷，省出卷时间",
            ],
          },
        ],
      },
      qa: {
        key: "QA",
        name: "常见问题(FAQs)",
        list: [
          {
            Q: "个人入驻流程是怎么样的?",
            A: "点击本页面<a href='https://web.zhixinoj.com/biz/' target='_blank'>免费试用</a>即可机构入驻，如遇任何问题可加下方微信或点击右侧电话咨询",
          },
          {
            Q: "机构入驻流程是怎样的？",
            A: "点击本页面<a href='https://web.zhixinoj.com/biz/' target='_blank'>免费试用</a>即可机构入驻，如遇任何问题可加下方微信或点击右侧电话咨询",
          },
          {
            Q: "直播间最多可多少人在线，延时时间是多少?",
            A: "为保证最佳教学效果，我们的直播间支持万人同时稳定在线，并采用超低延时技术，音画同步如面对面交流。实际承载力和具体延时因配置而异，您可以在<a href='https://web.zhixinoj.com/biz/' target='_blank'>免费试用</a>中亲自测试体验。",
          },
          {
            Q: "入驻平台后，课酬是否会出现拖欠?",
            A: "入驻后完成课程，即可在机构管理点击提现，点击<a href='https://web.zhixinoj.com/biz/' target='_blank'>免费试用</a>，立即入驻。",
          },
          {
            Q: "直播和回放课程是否有防盗和防录制?",
            A: "我们为您的知识产权提供了多重防护，包括跑马灯水印、防翻录机制等技术，有效防止非法扩散。点击<a href='https://web.zhixinoj.com/biz/' target='_blank'>免费试用</a>，查看课程防护效果",
          },
        ],
      },
      wechatEntry: {
        key: "WechatEntry",
        name: "扫 描 下 方 二 维 码 微 信 ， 1 对 1 专 属 顾 问 帮 您 搞 定 机 构 入 驻 ",
        desc: "扫描左侧微信添加专属顾问\n或点击下方\"免费试用\"直接体验",
        qr_code: getCdnUrl("/cdn/static/images/kehu_zixun_qrcode.png"),
        bg_img_left: getCdnUrl("/cdn/org/settled/bg-img-left.png"),
        bg_img_right: getCdnUrl("/cdn/org/settled/bg-img-right.png"),
      },
      aiPlatformImg: getCdnUrl("/cdn/org/settled/ai-platform.png"),
    };
  },
  computed: {
    ...mapGetters(["isAuthenticated", "user"]),
    heroSectionStyle() {
      if (!this.aiPlatform || !this.aiPlatform.bg_img) {
        return {
          "--hero-bg-image": "none",
        };
      }
      return {
        "--hero-bg-image": `url(${this.aiPlatform.bg_img})`,
      };
    },
    activeLiveRoomItem() {
      if (!this.liveRoom || !Array.isArray(this.liveRoom.list)) {
        return null;
      }
      return this.liveRoom.list[this.activeLiveRoomIndex] || null;
    },
    activeHomeworkItem() {
      if (!this.homework || !Array.isArray(this.homework.list)) {
        return null;
      }
      return this.homework.list[this.activeHomeworkIndex] || null;
    },
    realStories() {
      if (!Array.isArray(this.jsonData)) {
        return null;
      }
      return this.jsonData.find((item) => item.key === "RealStories");
    },
    storiesTopRow() {
      if (!this.realStories || !Array.isArray(this.realStories.list)) {
        return [];
      }
      const half = Math.ceil(this.realStories.list.length / 2);
      return this.realStories.list.slice(0, half);
    },
    storiesBottomRow() {
      if (!this.realStories || !Array.isArray(this.realStories.list)) {
        return [];
      }
      const half = Math.ceil(this.realStories.list.length / 2);
      const bottom = this.realStories.list.slice(half);
      if (!bottom.length) {
        return this.realStories.list.slice();
      }
      return bottom;
    },
    storiesTopRowLoop() {
      const top = this.storiesTopRow;
      return top.concat(top);
    },
    storiesBottomRowLoop() {
      const bottom = this.storiesBottomRow;
      return bottom.concat(bottom);
    },
    wechatEntryDescLines() {
      if (!this.wechatEntry || !this.wechatEntry.desc) {
        return [];
      }
      return this.wechatEntry.desc.split("\n").filter((line) => line.trim());
    },
  },
  watch: {
    aiPlatform: {
      handler(newVal) {
        if (newVal && newVal.name) {
          this.startHeroTitleAnimation(newVal.name);
          this.$nextTick(() => {
            this.initHeroImageAnimation();
          });
        }
      },
      immediate: true,
    },
    aiModule: {
      handler(newVal) {
        if (newVal) {
          this.startRotateAnimation();
        }
      },
      immediate: true,
    },
    homework: {
      handler(newVal) {
        if (newVal) {
          this.startHomeworkAutoSwitch();
        }
      },
      immediate: true,
    },
  },
  async created() {
    await this.apiGetOrgSettledJson();
  },
  async mounted() {
    this.initHeroImageAnimation();
    this.startRotateAnimation();
    this.startHomeworkAutoSwitch();
  },
  methods: {
    async apiGetOrgSettledJson() {
      const res = await getOrgSettledJson();
      this.jsonData = Array.isArray(res) ? res : [];
    },
    async handleFreeTrial(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (!this.isAuthenticated) {
        this.$loginDialog({
          isShowEquity: true,
          success: async () => {
            const userInfo = await getUserInfo();
            this.jumpOrgManagement(userInfo.data?.org_id);
            window.location.reload();
            return;
          },
        });
        return;
      }
      this.jumpOrgManagement();
    },
    async jumpOrgManagement(orgId = 0) {
      if (this.user?.org_id || orgId) {
        window.open(process.env.VUE_APP_ADMIN_URL, "_blank");
        return;
      }
      try {
        this.$loading();
        const orgInfo = await fastCreateOrganization();
        if (orgInfo.code === 1) {
          window.open(process.env.VUE_APP_ADMIN_URL, "_blank");
          window.location.reload();
        } else {
          this.$message.error(orgInfo.message);
        }
      } catch (error) {
        console.error("fastCreateOrganization failed", error);
        this.$message.error("机构创建失败，请稍后重试");
      } finally {
        this.$loading().close();
      }
    },
    handleSettle() {
      // window.location.href = `${process.env.VUE_APP_ADMIN_URL}`;
      this.consultVisible = true;
    },
    handleLiveRoomChange(index) {
      if (index === this.activeLiveRoomIndex) {
        return;
      }
      if (!this.liveRoom || !this.liveRoom.list || !this.liveRoom.list[index]) {
        return;
      }
      this.activeLiveRoomIndex = index;
    },
    handleLiveRoomKeydown(event, index) {
      if (
        event.key !== "Enter" &&
        event.key !== " " &&
        event.key !== "Spacebar"
      ) {
        return;
      }
      event.preventDefault();
      this.handleLiveRoomChange(index);
    },
    handleHomeworkTabChange(index) {
      if (
        index === this.activeHomeworkIndex ||
        !this.homework ||
        !this.homework.list ||
        !this.homework.list[index]
      ) {
        return;
      }
      this.activeHomeworkIndex = index;
      this.resetHomeworkAutoSwitch();
    },
    startHomeworkAutoSwitch() {
      this.stopHomeworkAutoSwitch();
      if (
        !this.homework ||
        !this.homework.list ||
        this.homework.list.length <= 1
      ) {
        return;
      }
      this.homeworkAutoSwitchTimer = setInterval(() => {
        const nextIndex =
          (this.activeHomeworkIndex + 1) % this.homework.list.length;
        this.activeHomeworkIndex = nextIndex;
      }, 5000);
    },
    stopHomeworkAutoSwitch() {
      if (this.homeworkAutoSwitchTimer) {
        clearInterval(this.homeworkAutoSwitchTimer);
        this.homeworkAutoSwitchTimer = null;
      }
    },
    resetHomeworkAutoSwitch() {
      this.stopHomeworkAutoSwitch();
      this.startHomeworkAutoSwitch();
    },
    handleHomeworkKeydown(event, index) {
      if (
        event.key !== "Enter" &&
        event.key !== " " &&
        event.key !== "Spacebar"
      ) {
        return;
      }
      event.preventDefault();
      this.handleHomeworkTabChange(index);
    },
    clearHeroTitleAnimation() {
      if (this.heroTitleAnimationTimer) {
        clearInterval(this.heroTitleAnimationTimer);
        this.heroTitleAnimationTimer = null;
      }
    },
    startHeroTitleAnimation(text) {
      this.clearHeroTitleAnimation();
      if (!text) {
        this.heroTitleDisplay = "";
        return;
      }
      this.heroTitleDisplay = "";
      let index = 0;
      this.heroTitleAnimationTimer = setInterval(() => {
        this.heroTitleDisplay = text.slice(0, index + 1);
        index += 1;
        if (index >= text.length) {
          this.clearHeroTitleAnimation();
        }
      }, 80);
    },
    initHeroImageAnimation() {
      if (this.heroImageScrollHandler) {
        window.removeEventListener("scroll", this.heroImageScrollHandler);
        this.heroImageScrollHandler = null;
      }
      this.$nextTick(() => {
        const imageEl = this.$refs.heroImage;
        if (!imageEl) {
          this.heroImageHighlighted = false;
          return;
        }
        let ticking = false;
        const updateHighlight = () => {
          if (!ticking) {
            window.requestAnimationFrame(() => {
              const scrollTop =
                window.pageYOffset || document.documentElement.scrollTop || 0;
              this.heroImageHighlighted = scrollTop >= imageEl.offsetTop - 200;
              ticking = false;
            });
            ticking = true;
          }
        };
        this.heroImageScrollHandler = updateHighlight;
        window.addEventListener("scroll", updateHighlight, { passive: true });
      });
    },
    handleQaItemClick(index) {
      const itemIndex = this.activeQaIndexes.indexOf(index);
      if (itemIndex === -1) {
        this.activeQaIndexes = [...this.activeQaIndexes, index];
        return;
      }
      this.activeQaIndexes = this.activeQaIndexes.filter(
        (activeIndex) => activeIndex !== index
      );
    },
    startRotateAnimation() {
      this.stopRotateAnimation();
      const baseAngle = this.aiModule?.rotate_angle
        ? parseFloat(this.aiModule.rotate_angle)
        : 0;
      this.rotateAngle = baseAngle;

      // 使用 requestAnimationFrame 优化动画性能
      let lastTime = 0;
      const animate = (currentTime) => {
        if (!this.rotateAnimationTimer) return;

        if (currentTime - lastTime >= 16) {
          this.rotateAngle += 1.5;
          if (this.rotateAngle >= 360) {
            this.rotateAngle = 0;
          }
          lastTime = currentTime;
        }
        this.rotateAnimationTimer = requestAnimationFrame(animate);
      };
      this.rotateAnimationTimer = requestAnimationFrame(animate);
    },
    stopRotateAnimation() {
      if (this.rotateAnimationTimer) {
        cancelAnimationFrame(this.rotateAnimationTimer);
        this.rotateAnimationTimer = null;
      }
    },
  },
  beforeDestroy() {
    this.clearHeroTitleAnimation();
    this.stopRotateAnimation();
    this.stopHomeworkAutoSwitch();
    if (this.heroImageScrollHandler) {
      window.removeEventListener("scroll", this.heroImageScrollHandler);
      this.heroImageScrollHandler = null;
    }
  },
};
</script>

<style lang="scss" scoped>
.org-settled-page {
  width: 100%;
  overflow-x: hidden;
}

/* Hero Image perspective animation */
.hero-image {
  transform-origin: center center;
  transform: perspective(1600px) rotateX(25deg);
  transition: transform 0.8s ease;
}

.hero-image--highlight {
  transform: perspective(1600px) rotateX(0deg);
}

@media (max-width: 768px) {
  .hero-image {
    transform: perspective(800px) rotateX(18deg);
  }
  .hero-image--highlight {
    transform: perspective(800px) rotateX(0deg);
  }
}

/* Homework slide transition */
.homework-slide-enter-active,
.homework-slide-leave-active {
  transition: all 0.4s ease;
}
.homework-slide-enter {
  opacity: 0;
  transform: translateY(20px);
}
.homework-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* FAQ accordion */
.qa-answer--closed {
  max-height: 0;
  opacity: 0;
}
.qa-answer--open {
  max-height: 500px;
  opacity: 1;
}
.qa-answer-text :deep(a) {
  color: #10b981;
  text-decoration: underline;
  font-weight: 600;
  &:hover {
    color: #059669;
  }
}

/* Stories marquee */
.stories-marquee {
  width: 100%;
  overflow: hidden;
}
.stories-row {
  display: flex;
  width: 100%;
  overflow: hidden;
  margin-bottom: 16px;
}
.stories-track {
  display: flex;
  gap: 16px;
  animation: marquee-scroll 40s linear infinite;
  width: max-content;
  padding: 8px 0;
}
.stories-row--bottom .stories-track {
  animation-direction: reverse;
  animation-duration: 45s;
}
.story-card {
  flex-shrink: 0;
  width: 320px;
  @media (max-width: 768px) {
    width: 280px;
  }
}
@keyframes marquee-scroll {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

/* AI rotating decoration (keep from original) */
.ssr-variant.hidden-1c0dkx6 {
  position: absolute;
  top: 0;
  right: -20%;
  bottom: 0;
  width: 60%;
  z-index: 0;
  opacity: 0.08;
  pointer-events: none;
}
html.dark .ssr-variant.hidden-1c0dkx6 {
  opacity: 0.05;
}
.framer-1stzqw2-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.framer-gzvjR {
  width: 600px;
  height: 600px;
  border-radius: 50%;
  position: relative;
  transform: rotate(var(--rotate-angle, 0deg));
}
.framer-gzvjR > div {
  position: absolute;
  border-radius: 50%;
}
.framer-1ehqw8y {
  inset: 0;
  border: 2px solid #10b981;
  opacity: 0.3;
}
.framer-1bce4jm {
  inset: 40px;
  border: 2px solid #3b82f6;
  opacity: 0.3;
}
.framer-xv7igh {
  inset: 80px;
  border: 2px solid #8b5cf6;
  opacity: 0.3;
}
.framer-xknqa4 {
  inset: 120px;
  border: 2px solid #f59e0b;
  opacity: 0.3;
}
.framer-1p1pzny {
  inset: 160px;
  border: 2px solid #ef4444;
  opacity: 0.3;
}
.framer-166olkj {
  inset: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.framer-166olkj > div {
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
}
.framer-1socfl3 { background: #10b981; top: -6px; left: 50%; }
.framer-y8aghn { background: #3b82f6; top: 20%; right: -6px; }
.framer-1w9dmf0 { background: #8b5cf6; bottom: 20%; right: -6px; }
.framer-88dz9y { background: #f59e0b; bottom: -6px; left: 50%; }
.framer-trql9i { background: #ef4444; bottom: 20%; left: -6px; }
.framer-138py8g { background: #06b6d4; top: 20%; left: -6px; }
.framer-1yx3zku { background: #ec4899; top: 50%; left: -6px; }
.framer-19bj41o {
  inset: 240px;
  border: 2px dashed #cbd5e1;
  opacity: 0.3;
}

</style>
